import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { NgClass, NgStyle } from '@angular/common';
import { delay, distinctUntilChanged } from 'rxjs/operators';
import {
  AuthenticationService,
  CropMetricsService,
  DeviceService,
  EquipmentService,
  GeneralUtilsService,
  StoreModelService,
  UserService,
} from '../../common/_services';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { ThemeService } from '../../common/_theme/theme.service';
import { environment } from '../../../environments/environment';
import { SM } from '../../../assets/data/enum/storeModel.enum';
import { ButtonAction } from '../../../assets/data/enum/buttonAction';

@Component({
  selector: 'app-x-chat-support',
  templateUrl: './x-chat-support.component.html',
  styleUrls: ['./wp-intercom-slack.css', './x-chat-support.component.scss'],
  imports: [NgClass, NgStyle],
})
export class XChatSupportComponent implements OnInit, OnDestroy, OnChanges {
  @Input() publicMode = true;

  private sanitizer = inject(DomSanitizer);
  private translate = inject(TranslateService);
  private cropMetricsService = inject(CropMetricsService);
  private deviceService = inject(DeviceService);
  private equipmentService = inject(EquipmentService);
  private authenticationService = inject(AuthenticationService);
  private themeService = inject(ThemeService);
  private userService = inject(UserService);
  private generalUtilsService = inject(GeneralUtilsService);
  private storeModelService = inject(StoreModelService);

  private subscriptions = new Subscription();
  private chatDataSub?: Subscription;
  initialized: boolean = false;

  isChatAllowed: boolean = false;
  isActive: boolean = false;
  baseUrlForChat: string;
  urlSafe: SafeResourceUrl;
  isLoggedIn$: Observable<boolean>;
  blockReloadChat: boolean = false;
  showChatButton: boolean = false;
  private privateModeInitialized: boolean = false;

  ngOnInit(): void {
    this.subscriptions.add(
      this.themeService.getVersion().subscribe((data) => {
        const version = data.version.split('|')[0].trim();
        const lng = this.translate.currentLang;

        this.isLoggedIn$ = this.authenticationService.isLoggedIn;

        const base =
          ThemeConfig.chatServiceProvider === 'slack'
            ? environment.chatSlackUrlSrevice
            : environment.chatSFUrlSrevice + '?';

        this.baseUrlForChat =
          base +
          '&v=' +
          version +
          '&ct=WebAPP' +
          '&f=' +
          ThemeConfig.brand.toLowerCase() +
          '&lang=' +
          lng +
          '&url=' +
          window.location.href;
      })
    );

    this.subscriptions.add(
      this.authenticationService.isLoggedIn.pipe(distinctUntilChanged()).subscribe(() => {
        this.blockReloadChat = false;
        if (this.isActive) {
          this.loadData();
        }
      })
    );

    this.subscriptions.add(
      this.generalUtilsService.chatActionObservable$.pipe(delay(0)).subscribe((action) => {
        if (!this.isChatAllowed) return;

        if (action === 'open') this.openChat(true);
        if (action === 'toggle') this.toggleChat();
        if (action === 'show') this.showChatButton = true;
        if (action === 'hide' && !this.isActive) this.showChatButton = false;
      })
    );

    this.initialized = true;
    if (!this.publicMode) {
      this.setupPrivateMode();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes['publicMode'] && !changes['publicMode'].currentValue) {
      this.setupPrivateMode();
      this.blockReloadChat = false;

      this.loadData();
    }
  }

  private setupPrivateMode(): void {
    if (this.privateModeInitialized) return;
    this.privateModeInitialized = true;

    this.subscriptions.add(
      this.storeModelService.list.subscribe((list) => {
        if (list?.length > 0) {
          if (
            this.storeModelService.enableActionByStoreModelAndAction(
              SM.SM_CHAT_SUPPORT,
              ButtonAction.READ
            )
          ) {
            this.isChatAllowed = true;
            this.generalUtilsService.showChatSupportElement();
          } else {
            this.isChatAllowed = false;
            this.generalUtilsService.hideChatSupportElement();
          }
        }
      })
    );
  }

  toggleChat(): void {
    this.isActive = !this.isActive;
    this.isActive ? this.loadData() : this.closeChat();
  }

  openChat(force = false): void {
    if (!this.isActive || force) {
      this.isActive = true;
      this.loadData();
    }
  }

  closeChat(): void {
    this.isActive = false;
  }

  loadData(): void {
    if (!this.isLoggedIn$) return;
    if (this.blockReloadChat) return;

    if (this.publicMode) {
      this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.baseUrlForChat + '&l=false'
      );
      this.blockReloadChat = true;
      return;
    }

    this.chatDataSub?.unsubscribe();

    this.chatDataSub = combineLatest([
      this.isLoggedIn$,
      this.userService.getUserMeFromLocalStore(),
      this.cropMetricsService.totFarm,
    ]).subscribe(([logged, user, totFarm]) => {
      if (!logged || !user) {
        this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.baseUrlForChat + '&l=false'
        );
        this.blockReloadChat = true;
        return;
      }

      const machinery = this.equipmentService.getConnectedEquipments()?.length || 0;
      const sensors = this.deviceService.getCounterSensors() || 0;
      const ws = this.deviceService.getCounterWeatherStation() || 0;

      const url =
        this.baseUrlForChat +
        '&l=true' +
        '&m=' +
        user.username +
        '&u=' +
        user.id +
        '-' +
        user.farm.id +
        '&firstN=' +
        encodeURIComponent(user.firstName) +
        '&lastN=' +
        encodeURIComponent(user.lastName) +
        '&ns=' +
        (sensors + ws) +
        '&tha=' +
        (totFarm?.toFixed(0) || 0) +
        '&ce=' +
        machinery;

      this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.blockReloadChat = true;
    });
    this.subscriptions.add(this.chatDataSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
